import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { emitChatNotification } from "src/notifications/notification-center/chatNotificationBus";
import { useSocket } from "../../context/SocketContext";

const USE_MOCK_JOB_CHAT_NOTIFICATION = false; // true = mensaje de prueba

type JobChatEvent = {
  jobEventUid?: string;
  jobAlias?: string | null;
  message?: {
    uid?: string;
    companyId?: number;
    sender?: { uid?: string; type?: string; name?: string; firstName?: string; lastName?: string; businessName?: string };
    content?: { category?: string; text?: string; name?: string };
  };
};

const senderName = (event: JobChatEvent) => {
  const sender = event.message?.sender;
  return sender?.businessName || sender?.name || [sender?.firstName, sender?.lastName].filter(Boolean).join(" ") || "New message";
};

const messagePreview = (event: JobChatEvent) => {
  const content = event.message?.content;
  if (content?.text) return content.text;
  if (content?.category === "image") return content.name || "Sent an image";
  if (content?.category === "audio") return "Sent an audio message";
  return content?.name || "Sent a file";
};

export function JobChatNotification() {
  const { socket } = useSocket();
  const { uid, workforces, activeCompanyId, companies, company } = useSelector((state: any) => state.userState);
  const activeCompanyIdRef = useRef(activeCompanyId);
  activeCompanyIdRef.current = activeCompanyId;

  const companyNameById = useMemo(() => {
    const names = new Map<string, string>();

    (companies ?? []).forEach((membership: any) => {
      if (membership?.companyId == null) return;
      const name = membership.company?.businessName || membership.company?.name;
      if (name) names.set(String(membership.companyId), name);
    });

    return names;
  }, [companies]);

  const showIncomingMessage = useCallback(
    (event: JobChatEvent) => {
      const sender = event.message?.sender;
      if (!event.message || !event.jobEventUid) return;

      const workforceUids = (workforces || []).map((workforce: any) => workforce.uid);
      const isOwn = sender?.uid && ((sender.type === "user" && sender.uid === uid) || workforceUids.includes(sender.uid));
      if (isOwn) return;

      const messageCompanyId = event.message.companyId ?? activeCompanyIdRef.current ?? null;

      emitChatNotification({
        id: event.message.uid ?? event.jobEventUid,
        kind: "job-chat",
        companyId: messageCompanyId,
        companyName: companyNameById.get(String(messageCompanyId)) ?? company?.businessName ?? null,
        title: senderName(event),
        body: messagePreview(event),
        chatName: event.jobAlias ?? senderName(event),
        focusMessageUid: event.message.uid ?? null,
        focusMessageText: messagePreview(event),
        target: {
          type: "job",
          jobEventUid: event.jobEventUid,
          companyId: messageCompanyId,
        },
      });
    },
    [uid, workforces, companyNameById, company?.businessName],
  );

  useEffect(() => {
    if (USE_MOCK_JOB_CHAT_NOTIFICATION || !socket) return;
    socket.on("jobChatMessage", showIncomingMessage);
    return () => socket.off("jobChatMessage", showIncomingMessage);
  }, [socket, showIncomingMessage]);

  useEffect(() => {
    if (!USE_MOCK_JOB_CHAT_NOTIFICATION) return;

    const timer = setTimeout(
      () =>
        showIncomingMessage({
          jobEventUid: "D03f1b73-F6be-4cfd-9b3a-1f87ea6962b3",
          message: {
            uid: `mock-job-chat-${Date.now()}`,
            sender: { uid: "mock-user", type: "user", name: "New message in Job Chat" },
            content: { category: "plainText", text: "Hola, este es un mensaje de prueba del Job Chat" },
          },
        }),
      1500,
    );

    return () => clearTimeout(timer);
  }, [showIncomingMessage]);

  return null;
}
